/**
 * Роли организации — единственное место, где роль превращается в текст.
 *
 * ★ Читать словарь ТОЛЬКО через `roleBadge`. `Record<Role, …>` держит
 * полноту на этапе сборки, но само значение приезжает С СЕРВЕРА, и бэк
 * может начать отдавать новую роль раньше, чем выкатится фронт. Прямое
 * `ROLE_LABELS[user.role].label` на незнакомом значении даёт TypeError, а
 * он в шапке сайдбара — это белая страница на всём приложении. Ровно так
 * и вышло с `finance_manager`.
 */

import type { AppAccessItem, AssignableRole, Role } from "./types";

export type RoleBadge = { label: string; color: string };

const ROLE_LABELS: Record<Role, RoleBadge> = {
  admin: { label: "Account Admin", color: "var(--c-accent-700)" },
  ads_manager: { label: "Ads Manager", color: "var(--c-info-500)" },
  finance_manager: { label: "Finance Manager", color: "var(--c-pos-600)" },
  viewer: { label: "Viewer", color: "var(--c-text-3)" },
};

/** Роль, которой фронт ещё не знает. Показываем нейтральное слово вместо
 *  падения — доступ всё равно решает сервер, а не эта надпись. */
const UNKNOWN_ROLE: RoleBadge = { label: "Member", color: "var(--c-text-3)" };

export function roleBadge(role: string): RoleBadge {
  return (ROLE_LABELS as Record<string, RoleBadge>)[role] ?? UNKNOWN_ROLE;
}

/** Порядок и подписи селектора роли в форме приглашения. */
export const ASSIGNABLE_ROLES: { value: AssignableRole; hint: string }[] = [
  {
    value: "admin",
    hint: "manages users, integrations, billing + all apps",
  },
  {
    value: "ads_manager",
    hint: "works the apps they are granted below",
  },
  {
    value: "viewer",
    hint: "view only — sees the apps granted below, can't change anything",
  },
  {
    value: "finance_manager",
    hint: "billing and organization details; ads only where granted below",
  },
];

/** Роль участника → значение селектора. Незнакомое сводится к
 *  `ads_manager` — «участник», доступ решает пер-приложенческая таблица (§51). */
export function assignableRoleOf(role: string): AssignableRole {
  return role === "admin" || role === "finance_manager" || role === "viewer"
    ? role
    : "ads_manager";
}

/** Viewer — только просмотр: уровни правки по приложениям снижаются до
 *  View Only. Сервер отклоняет их с `viewer_cannot_write`, поэтому форма
 *  снижает сама в момент выбора роли. */
export function capAccessForRole<T extends Record<number, AppAccessItem["level"]>>(
  role: AssignableRole,
  access: T
): T {
  if (role !== "viewer") return access;
  const out = { ...access } as Record<number, AppAccessItem["level"]>;
  for (const k of Object.keys(out)) {
    const id = Number(k);
    if (out[id] === "ads_manager" || out[id] === "admin") out[id] = "view";
  }
  return out as T;
}

/** Роли, которые значат что-то ПОМИМО пер-приложенческого доступа, — их
 *  показываем значком в таблице участников. `ads_manager` как роль аккаунта
 *  не добавляет ничего сверх уровней по приложениям; `viewer` добавляет
 *  запрет правок, поэтому значок у него есть. */
export const ACCOUNT_LEVEL_ROLES: readonly Role[] = ["admin", "finance_manager", "viewer"];

/** Выдано ли участнику хоть одно приложение (любой уровень кроме Hidden).
 *  Account Admin строк не носит и держит все приложения неявно (§51). */
export function hasAnyAppAccess(user: {
  role: Role;
  app_access?: AppAccessItem[];
}): boolean {
  if (user.role === "admin") return true;
  return (user.app_access ?? []).some((a) => a.level !== "hidden");
}
